import { Injectable, NgZone } from '@angular/core';
import { AudioData } from '@weavix/models/src/media/media';
import { PubSubService } from 'weavix-shared/services/pub-sub.service';
import { environment } from 'environments/environment';
import { Topic } from '@weavix/models/src/topic/topic';
import { ProfileService } from 'weavix-shared/services/profile.service';
import { sleep } from 'weavix-shared/utils/sleep';
import { LocalDeviceUtility } from 'weavix-shared/utils/local-device-utility';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AnalyticsService, StAction, StObject } from './analytics.service';
import { Channel } from 'models-mobx/channels-store/channel';
import { myUser } from 'models-mobx/users-store/users-store';
import { StopWatch } from '@weavix/utils/src/stopwatch';
import { myCompany } from 'models-mobx/companies-store/companies-store';
import { generateMeshCompatibleId } from '@weavix/domain/src/mesh/get-mesh-id';
import { filter, map, merge, Observable } from 'rxjs';
import { PushPayload, PushTopic } from '@weavix/models/src/push/push';
import { MqttQos } from '@weavix/mqtt/src/models/mqtt-qos';
import { PttDenialReleasedReason } from '@weavix/telemetry';

/** Why a press was cancelled before the microphone opened. Derived from the telemetry contract so a reason cannot be added on one side only. */
export type PttCancelReason = PttDenialReleasedReason;

/** Why a PTT gesture ended. Only 'released' is an actual key release; 'superseded' has no pttUp behind it, so it is not one of these. */
export type PttEndReason = Exclude<PttDenialReleasedReason, 'superseded'>;

export const MAX_PTT_TIME = 300_000;
const PTT_TIMEOUT = 15_000;

/** Shape of the `window.__weavixPttDebug` capture object (see beginPttAudioDebug). */
interface PttAudioHeard {
    id: string;
    accountId: string | null;
    channelId: string | null;
    sampleCount: number;
    durationSeconds: number;
    peak: number;
    rms: number;
    /** Internal running sum of squares used to compute rms incrementally. */
    sumSquares: number;
    startedAt: number;
}
interface PttAudioDebug {
    enabled: boolean;
    current: PttAudioHeard | null;
    last: PttAudioHeard | null;
}

/** How long a PTT counts as busy after the button is pressed, covering the wait for the microphone (up to 2 s). */
export const PTT_START_BUSY_MS = 3_000;
/** How long a PTT counts as busy after it stops recording, while its last audio and end signal are sent. */
export const PTT_END_BUSY_MS = 1_000;
@Injectable({ providedIn: 'root' })
export class PttService {
    private assets: { [key: string]: HTMLAudioElement } = {};

    playing: boolean;
    playingId: string;
    recording: boolean;
    /** Until when a PTT being started or ended still counts as busy: before the mic opens, and while its last audio and end signal go out. */
    private transmitBusyUntil = 0;

    /** Keeps the PTT busy while its last audio (`wait`: 500 ms more) and end signal are sent. */
    holdForTransmitEnd(wait: boolean) {
        this.transmitBusyUntil = Date.now() + (wait ? 500 : 0) + PTT_END_BUSY_MS;
    }

    private readonly enders: Array<{ func: () => Promise<void> }> = [] as any;
    private context: AudioContext;
    private destination: MediaStreamAudioDestinationNode;
    contextAudio: HTMLAudioElement;
    private audioObservable: Observable<{ payload: AudioData, buffer: Buffer | null }>;

    constructor(
        private readonly pubSubService: PubSubService,
        private readonly profileService: ProfileService,
        private readonly zone: NgZone,
        private readonly deviceService: DeviceDetectorService,
    ) {
        // Expose the PTT audio-capture hook eagerly so tooling/support can detect it is available
        // BEFORE any audio has played. The per-transmission stats are filled in by
        // beginPttAudioDebug/updatePttAudioDebug during playback.
        const w = window as unknown as { __weavixPttDebug?: PttAudioDebug };
        if (!w.__weavixPttDebug) w.__weavixPttDebug = { enabled: true, current: null, last: null };
        this.pubSubService.setPttBusyCheck(() => this.recording || this.playing || Date.now() < this.transmitBusyUntil);
    }

    prepare() {
        const prepare = () => {
            this.preplayAudio();
            document.removeEventListener('mousedown', prepare);
            document.removeEventListener('touchstart', prepare);
            document.removeEventListener('keydown', prepare);
        };
        document.addEventListener('mousedown', prepare);
        document.addEventListener('touchstart', prepare);
        document.addEventListener('keydown', prepare);
    }

    async subscribeAudio(component: unknown) {
        const user = await this.profileService.getUserProfile(component);
        this.audioObservable = this.pubSubService.subscribe<Buffer>(component, Topic.UserAudioStream, [user.id], true, 0, true)
                .pipe(map(data => {
                    const headerLength = data.payload.readUInt32BE(0);
                    const payload = JSON.parse(data.payload.slice(4, 4 + headerLength).toString('utf8')) as AudioData;
                    const buffer = data.payload.slice(4 + headerLength);
                    return { payload, buffer };
                }));
        return merge(
            this.audioObservable,
            this.pubSubService.subscribe<PushPayload>(component, Topic.UserNotification, [user.id], false, 0)
                .pipe(
                    filter(data => data.payload.topic === PushTopic.Ptt),
                    map(data => {
                        const payload = data.payload.data as AudioData;
                        return { payload, buffer: null as Buffer | null };
                    }),
                ),
        );
    }

    async startPtt(component, channel: Channel, onTimeout: () => void) {
        return await this.startPttInternal(component, {
            channelId: channel.id,
            accountId: channel.accountId,
            date: new Date().toISOString(),
            fromTeams: environment.teamsApp,
        }, channel, onTimeout);
    }

    async startPttInternal(component, data: Partial<AudioData>, channel: Channel = null, onTimeout: () => void) {
        if (!this.context) this.preplayAudio();
        if (this.playing) {
            this.playAsset('busy-tone');
            return false;
        }
        this.transmitBusyUntil = Date.now() + PTT_START_BUSY_MS;

        const buttonDelayStopWatch = new StopWatch().start();
        let started = false;
        let startResolve;
        const startPromise = new Promise(resolve => startResolve = resolve);
        let closed = false;
        const pttId = generateMeshCompatibleId(myUser().id);

        const stopTracks = () => {
            stream.getTracks().forEach(track => track.stop());
        };

        const close = async (wait: boolean) => {
            this.holdForTransmitEnd(wait);
            if (wait) await sleep(500);
            this.recording = false;

            source.disconnect();
            processor.disconnect();

            worker.postMessage({ command: 'record', id: pttId });

            stopTracks();

            await sleep(50);
            this.sendAudio(this, data.accountId, pttId);

            this.playAsset('nextel-beep-stop', { timeout: 1000 });

            AnalyticsService.track(StObject.Ptt, StAction.Pressed, this.constructor.name, {
                duration: Date.now() - new Date(data.date).getTime(),
                channelId: data.channelId,
                accountId: data.accountId,
                destinationLanguages: channel?.channelLanguages,
                memberCount: channel?.users?.length,
                channelType: channel?.type,
                sourceLanguage: myUser().locale,
                sourceLocale: myUser().locale,
                sourceDialect: myUser().dialect,
            });
        };

        this.enders.push({ func: async () => {
            closed = true;
            const wait = started;
            await startPromise;
            await close(wait);
        } });

        // windows sucks and cannot handle 48000
        const sampleRate = this.deviceService.getDeviceInfo().os.toLowerCase() === 'windows' ? 16_000 : 48_000;

        const device = await LocalDeviceUtility.getDefaultAudioInputDevice();
        // This handles a case where you can get a permission prompt but the keyup event is not fired to cancel the ptt.
        // This will detect that getting the stream is taking longer than it should (for the mic permission prompt) and cancel
        const stream = await Promise.race([
            LocalDeviceUtility.getDeviceStream(device.deviceId || true, false, { audio: { sampleRate, channelCount: 1 } }),
            sleep(2000).then(() => null), // Safari can take >1s to get the stream on the first call
        ]);
        if (!stream) throw new Error('No permission');

        if (closed) {
            stopTracks();
            return false;
        }

        this.recording = true;
        setTimeout(() => !closed && onTimeout(), MAX_PTT_TIME);

        let context: AudioContext = null;
        let source: MediaStreamAudioSourceNode = null;
        let worker: Worker = null;
        let processor: ScriptProcessorNode;

        this.zone.runOutsideAngular(() => {
            worker = new Worker('/assets/js/audio/audio-worker.js');
            worker.onmessage = event => {
                switch (event.data.command) {
                    case 'data':
                        this.sendAudio(this, data.accountId, pttId, event.data.blob);
                        break;
                }
            };

            try {
                context = new (window.AudioContext || window['webkitAudioContext'])({ sampleRate });
                source = context.createMediaStreamSource(stream);
            } catch (e) {
                context = new (window.AudioContext || window['webkitAudioContext'])();
                source = context.createMediaStreamSource(stream);
            }
        });

        const frequency = context.sampleRate;
        const senderAckStopWatch = new StopWatch().start();
        const firstRoundTripPromise = this.prepareAudio(this, data.accountId, {
            id: pttId,
            frequency,
            ...data,
        });

        const onTelemetry = () => {
            if (buttonDelayStopWatch.isRunning() || senderAckStopWatch.isRunning()) return;
            this.sendTelemetry('sent', {
                id: pttId,
                accountId: channel?.accountId,
                channelId: channel?.id,
                locale: myUser().locale,
                dialect: myUser().dialect,
            }, {
                senderAckTime: senderAckStopWatch.elapsed(),
                buttonDelay: buttonDelayStopWatch.elapsed(),
            });
        };

        firstRoundTripPromise.then(() => {
            senderAckStopWatch.stop();
            onTelemetry();
        });

        await this.playAsset('nextel-beep-start', { timeout: 1000 });
        await sleep(50);
        started = true;

        buttonDelayStopWatch.stop();
        onTelemetry();

        this.zone.runOutsideAngular(() => {
            processor = (context.createScriptProcessor || context['createJavaScriptNode']).call(context, 8192, 1, 1);
            source.connect(processor);
            processor.connect(context.destination);

            processor.onaudioprocess = function (event) {
                if (!started) return;
                const buffer = [event.inputBuffer.getChannelData(0)];
                worker.postMessage({ command: 'record', id: pttId, buffer, sampleRate: event.inputBuffer.sampleRate || frequency, channels: 1 });
            };
        });

        startResolve();
        return true;
    }

    async endPtt() {
        let nextEnder = this.enders.shift();
        while (nextEnder) {
            await Promise.race([nextEnder.func(), sleep(600)]);
            nextEnder = this.enders.shift();
        }
    }

    // Needed for safari
    preplayAudio() {
        try {
            if (!this.context) {
                this.context = new (window.AudioContext || window['webkitAudioContext'])({ sampleRate: 48_000 });
                this.destination = this.context.createMediaStreamDestination();
            }
            if (!this.contextAudio) {
                this.contextAudio = new Audio();
                this.contextAudio.srcObject = this.destination.stream;
            }
            this.contextAudio.play();

            const buffer = this.context.createBuffer(1, 1, 22_050);
            const source = this.context.createBufferSource();
            source.buffer = buffer;
            source.connect(this.destination);
            source.start(0);
        } catch (e) {
            console.error(e);
        }

        this.testAudio('busy-tone');
        this.testAudio('electronic-buzz-buzz');
        this.testAudio('nextel-beep-start');
        this.testAudio('nextel-beep-stop');
    }

    playStream(stream: MediaStream) {
        if (!this.context) this.preplayAudio();
        this.contextAudio.srcObject = stream;
        this.contextAudio.play();
    }

    async prepareAudio(component, accountId: string, data: AudioData) {
        const user = await this.profileService.getUserProfile(component);
        await this.pubSubService.publish(accountId ? Topic.AccountAudioStart : Topic.UserAudioStart, [accountId ?? user.id, data.id], {
            locale: user.locale,
            dialect: user.dialect,
            ...data,
        });
    }

    async sendAudio(component, accountId: string, id: string, data?) {
        const user = await this.profileService.getUserProfile(component);
        const qos = data ? MqttQos.none : MqttQos.atLeastOnce;
        await this.pubSubService.publish(accountId ? Topic.AccountAudioSend : Topic.UserAudioSend, [accountId ?? user.id, id], data || new Uint8Array(0), qos);
    }

    async playAudio(component, accountId: string, audioData: AudioData, listener?: (offset: number) => void) {
        const { id } = audioData;

        if (!this.context) this.preplayAudio();

        if (this.playing && this.playingId === id) return;

        if (this.recording || this.playing) {
            const reason = this.playing ? 'ptt-receiving' : 'ptt-sending';
            console.log(`skipPtt ${reason}`);
            this.sendTelemetry('skipped', audioData, { reason });
            return;
        }

        this.playing = true;
        this.playingId = id;

        this.beginPttAudioDebug(id, accountId, audioData);

        this.sendTelemetry('received', audioData, { isPlayback: false, isRestart: false });
        let subs;
        const channels = audioData?.channels ?? 1;
        try {
            let closed = false;
            const close = async () => {
                if (closed) return;
                closed = true;

                if (subs) subs.unsubscribe();
                await sleep(Math.max(1, offset - this.context.getOutputTimestamp().contextTime));
                this.playing = false;
                this.endPttAudioDebug();
            };
            setTimeout(close, MAX_PTT_TIME);
            let timeout = setTimeout(close, PTT_TIMEOUT);

            const outputDevice = await LocalDeviceUtility.getDefaultAudioOutputDevice();
            if (outputDevice?.deviceId) (this.contextAudio as any).setSinkId?.(outputDevice.deviceId);

            this.destination = this.context.createMediaStreamDestination();
            this.contextAudio.srcObject = this.destination.stream;
            this.contextAudio.play();

            let offset = 0;
            let pending = [];
            let pendingLength = 0;
            let addedLength = 0;
            let pendingFrames = 0;

            subs = this.audioObservable.subscribe(async data => {
                if (data.payload.id !== id || data.buffer == null) return;

                clearTimeout(timeout);
                timeout = setTimeout(close, PTT_TIMEOUT);

                const payload = data.buffer;
                if (!payload.length) close();

                pending.push(payload);
                pendingLength += payload.length;
                addedLength += payload.length;
                if (!payload.length && pendingLength >= 2048 && addedLength > 0 || addedLength >= 4096) {
                    const buf = new Uint8Array(pendingLength);
                    for (let i = 0, j = 0; i < pendingLength && j < pending.length; j++) {
                        buf.set(pending[j], i);
                        i += pending[j].length;
                    }

                    let buffer: AudioBuffer;
                    if (audioData?.pcm) {
                        const length = buf.length / channels / 2;
                        buffer = this.context.createBuffer(channels, length, audioData.frequency);
                        for (let j = 0; j < channels; j++) {
                            const channel = buffer.getChannelData(j);
                            for (let i = 0; i < length; i++) {
                                // eslint-disable-next-line no-bitwise
                                const ui = buf[(i * channels + j) * 2] + (buf[(i * channels + j) * 2 + 1] << 8);
                                channel[i] = (ui >= 32_768 ? ui - 65_536 : ui) / 32_768;
                            }
                        }
                        pending = [];
                        pendingLength = 0;
                        addedLength = 0;
                    } else {
                        const indexes = [];
                        for (let i = 0; i < buf.length - 6; i++) {
                            // eslint-disable-next-line no-bitwise
                            if (buf[i] === 0xFF && (buf[i + 1] & 0xF6) === 0xF0) {
                                indexes.push(i);
                                // eslint-disable-next-line no-bitwise
                                const frameLength = ((buf[i + 3] & 3) << 11) + (buf[i + 4] << 3) + (buf[i + 5] >> 5) - 7;
                                i += frameLength - 1;
                            }
                        }
                        const previousFrames = pendingFrames;
                        const postFrames = 1;
                        pendingFrames = Math.min(indexes.length, 4) - postFrames;
                        pending = [buf.slice(indexes[indexes.length - pendingFrames - postFrames])];
                        pendingLength = pending[0].length;
                        addedLength = 0;

                        buffer = await new Promise(resolve => {
                            this.context.decodeAudioData(buf.buffer, resolve, err => console.error(err, indexes));
                        });
                        const samplesPerFrame = Math.round(buffer.length / indexes.length);
                        if (buffer.length > samplesPerFrame * (previousFrames + postFrames)) {
                            const newBuffer = this.context.createBuffer(buffer.numberOfChannels, buffer.length - samplesPerFrame * (previousFrames + postFrames), buffer.sampleRate);
                            for (let j = 0; j < buffer.numberOfChannels; j++) {
                                const channel = buffer.getChannelData(j);
                                const newChannel = newBuffer.getChannelData(j);
                                newChannel.set(channel.slice(samplesPerFrame * previousFrames, buffer.length - postFrames * samplesPerFrame));
                            }
                            buffer = newBuffer;
                        } else {
                            return;
                        }
                    }

                    const currentTime = this.context.getOutputTimestamp().contextTime;
                    offset = Math.max(offset, currentTime + 0.5);

                    const source = this.context.createBufferSource();
                    source.buffer = buffer;
                    source.connect(this.destination);
                    source.start(offset);
                    offset += buffer.duration;

                    // Tap the decoded audio (this is exactly what gets played to the speaker) so
                    // e2e tests can verify the PTT was actually heard.
                    this.updatePttAudioDebug(buffer);

                    if (listener) listener(offset);
                }
            });

            return close;
        } catch (e) {
            console.error(e);
            this.contextAudio.src = `${environment.is360Api}/a/${accountId}/media/audio/${id}/stream.aac`;
            this.contextAudio.play();
        }
    }

    // region PTT audio debug capture (e2e "was it heard?" verification)

    /**
     * Starts capturing amplitude stats for an incoming PTT, exposing them on
     * `window.__weavixPttDebug`. Always active. This is the browser-side analog of the Walt
     * `-playback.pcm` file.
     */
    private beginPttAudioDebug(id: string, accountId: string, audioData: AudioData): void {
        const w = window as unknown as { __weavixPttDebug?: PttAudioDebug };
        if (!w.__weavixPttDebug) w.__weavixPttDebug = { enabled: true, current: null, last: null };
        w.__weavixPttDebug.current = {
            id,
            accountId: accountId ?? null,
            channelId: audioData?.channelId ?? null,
            sampleCount: 0,
            durationSeconds: 0,
            peak: 0,
            rms: 0,
            sumSquares: 0,
            startedAt: Date.now(),
        };
    }

    /** Accumulates peak/RMS from a decoded playback buffer (channel 0). */
    private updatePttAudioDebug(buffer: AudioBuffer): void {
        const current = (window as unknown as { __weavixPttDebug?: PttAudioDebug }).__weavixPttDebug?.current;
        if (!current) return;

        const data = buffer.getChannelData(0);
        let peak = current.peak;
        let sumSquares = current.sumSquares;
        for (let i = 0; i < data.length; i++) {
            const abs = Math.abs(data[i]);
            if (abs > peak) peak = abs;
            sumSquares += data[i] * data[i];
        }
        current.peak = peak;
        current.sumSquares = sumSquares;
        current.sampleCount += data.length;
        current.durationSeconds += buffer.duration;
        current.rms = current.sampleCount > 0 ? Math.sqrt(sumSquares / current.sampleCount) : 0;
    }

    /** Finalizes the current capture, moving it to `last`. */
    private endPttAudioDebug(): void {
        const dbg = (window as unknown as { __weavixPttDebug?: PttAudioDebug }).__weavixPttDebug;
        if (!dbg || !dbg.current) return;
        dbg.last = dbg.current;
        dbg.current = null;
    }

    // endregion

    async playAsset(file: string, options?) {
        return new Promise(resolve => {
            try {
                const asset = this.getAudio(file);
                asset.currentTime = 0;
                asset.muted = false;
                asset.loop = options?.loop ?? false;
                asset.play();
                asset.onended = resolve;
            } catch (e) {
                console.error(e);
            }
            if (options?.timeout) setTimeout(resolve, options?.timeout);
        });
    }

    isPlaying(file: string) {
        const asset = this.getAudio(file);
        return asset['isPlaying'];
    }

    async stopAsset(file: string) {
        const asset = this.getAudio(file);
        asset.pause();
    }

    testAudio(file: string) {
        try {
            const asset = this.getAudio(file);
            asset.muted = true;
            asset.currentTime = 0;
            asset.play();
        } catch (e) {
            // Ignore
        }
    }

    getAudio(file: string) {
        let asset = this.assets[file];
        if (!asset) {
            const src = `/assets/sounds/${file}.wav?ngsw-bypass=true`;
            asset = new Audio();
            asset.src = src;
            this.assets[file] = asset;
        }
        return asset;
    }

    /**
     * Reports a PTT press abandoned before the microphone was opened, and warns
     * the worker in the one case they have no other cue for.
     *
     * Published as `denial-released`, the same event mobile emits when a press is
     * given up before it transmits; `reason` separates this from the channel
     * contention case that event also covers.
     *
     * @param channel the channel the key was pressed on, captured at keydown so
     *                a channel switch during the press cannot misattribute it
     * @param overlap ms between the keydown and the cancel being detected, negative,
     *                matching how mobile reports overlap on this same event
     * @param reason what ended the press
     */
    async cancelBeforeStart(channel: Channel, overlap: number, reason: PttCancelReason): Promise<void> {
        // Losing focus is the only cancel with no other cue: the worker is still
        // holding the key and never heard a start beep.
        if (reason === 'focus-lost') this.playAsset('busy-tone');

        await this.sendTelemetry('denial-released', {
            id: undefined,
            accountId: channel?.accountId,
            channelId: channel?.id,
        }, { overlap, reason });
    }

    private async sendTelemetry(type: 'received' | 'sent' | 'sent-jitter' | 'jitter' | 'skipped' | 'buzzed' | 'denial-released', payload: AudioData, props: any) {
        try {
            const myId = myUser().id;
            await this.pubSubService.publish(Topic.PttTelemetry, [myId], {
                type,
                id: payload.id,
                channelId: payload.channelId,
                accountId: payload.accountId ?? myCompany(),
                isAdcMessage: !!payload.accountId,
                personId: payload.personId ?? myId,
                locale: payload.locale,
                dialect: payload.dialect,
                date: new Date().toISOString(),
                device: 'web',
                build: environment.version,
                ...props,
            });
        } catch (e) {
            console.warn(`Failed to publish PTT telemetry ${e?.message}`);
        }
    }
}
